import React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Hidden from "@mui/material/Hidden";
import { styled } from "@mui/material/styles";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { MARKETING_MENU_CONFIG, MOBILE_SECONDARY_LINKS } from "./marketingMenuConfig";
import { colors, radius } from "../RecipeDesigner/styles/designTokens";

const MobileRow = styled(MenuItem)({
    minHeight: 44,
    whiteSpace: "normal",
    alignItems: "flex-start",
    padding: "8px 12px",
    borderRadius: radius.md,
    margin: "1px 6px",
    transition: "background-color 120ms ease",
    "& svg": { color: colors.text.tertiary },
    "&:hover, &.Mui-focusVisible": { backgroundColor: colors.accent.primarySubtle },
});

const IconRail = ({ icon: Icon, size = 16, iconProps }) => (
    <Box
        component="span"
        sx={{ width: 22, minWidth: 22, mr: "12px", mt: "2px", display: "flex", justifyContent: "center" }}
    >
        <Icon size={size} {...(iconProps || {})} />
    </Box>
);

export default function MobileMarketingMenu({ handleMarketingMenuClose }) {

    const [expandedHref, setExpandedHref] = React.useState(null);
    const toggle = (href) => setExpandedHref((current) => (current === href ? null : href));

    const closeDrawer = () => {
        if (typeof handleMarketingMenuClose === "function") handleMarketingMenuClose();
    };

    return (
        <Box sx={{ py: "6px" }}>
            {MARKETING_MENU_CONFIG.sections.map((section) => {
                const isExpanded = expandedHref === section.href;
                return (
                    <React.Fragment key={section.href}>
                        <MobileRow component="a" href={section.href} onClick={closeDrawer} disableRipple>
                            <IconRail icon={section.icon} iconProps={{ strokeWidth: 1.75 }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: "block",
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                        lineHeight: 1.35,
                                        letterSpacing: "-0.01em",
                                        color: colors.text.primary,
                                    }}
                                >
                                    {section.label}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        mt: "2px",
                                        fontSize: "0.75rem",
                                        lineHeight: 1.45,
                                        color: colors.text.tertiary,
                                    }}
                                >
                                    {section.sublabel}
                                </Box>
                            </Box>
                            <IconButton
                                size="small"
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${section.label}`}
                                aria-expanded={isExpanded}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggle(section.href);
                                }}
                                sx={{
                                    p: "4px",
                                    ml: "4px",
                                    color: colors.text.tertiary,
                                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)", color: colors.accent.primary },
                                }}
                            >
                                <MdOutlineKeyboardArrowDown
                                    size={18}
                                    style={{
                                        transition: "transform 180ms cubic-bezier(0.4, 0, 0.2, 1)",
                                        transform: isExpanded ? "rotate(180deg)" : "none",
                                    }}
                                />
                            </IconButton>
                        </MobileRow>

                        <Collapse in={isExpanded} timeout={180} unmountOnExit>
                            <Box
                                sx={{
                                    ml: "27px",
                                    pl: "14px",
                                    borderLeft: `1px solid ${colors.accent.primaryBorder}`,
                                }}
                            >
                                {section.items.map((item) => (
                                    <MobileRow
                                        key={item.href}
                                        component="a"
                                        href={item.href}
                                        onClick={closeDrawer}
                                        disableRipple
                                        sx={{ minHeight: 36, py: "6px", mx: "2px" }}
                                    >
                                        <IconRail icon={item.icon} size={15} iconProps={item.iconProps || { strokeWidth: 1.75 }} />
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: "0.8125rem",
                                                fontWeight: 500,
                                                lineHeight: 1.35,
                                                color: colors.text.primary,
                                            }}
                                        >
                                            {item.label}
                                        </Box>
                                    </MobileRow>
                                ))}
                            </Box>
                        </Collapse>
                    </React.Fragment>
                );
            })}

            <Hidden mdUp>
                <Divider sx={{ my: "6px", borderColor: colors.border.subtle }} />

                {MOBILE_SECONDARY_LINKS.map((link) => (
                    <MobileRow
                        key={link.href}
                        component="a"
                        href={link.href}
                        onClick={closeDrawer}
                        disableRipple
                        sx={{ minHeight: 40, py: "8px" }}
                    >
                        <IconRail icon={link.icon} size={15} />
                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.8125rem",
                                fontWeight: 500,
                                lineHeight: 1.35,
                                color: colors.text.primary,
                            }}
                        >
                            {link.label}
                        </Box>
                    </MobileRow>
                ))}
            </Hidden>
        </Box>
    );
}
